import { z } from 'zod';

/**
 * Contracten van de module notifications: de inbox van de medewerker en zijn pushmeldingen.
 *
 * Eén regel loopt overal doorheen: **een melding bevat geen inhoud**, alleen een aanwijzing
 * dat er iets is (ADR-0038). Een pushmelding staat op een vergrendeld scherm dat op een
 * werkbank ligt; wat erin staat, kan iedereen lezen die langsloopt.
 */

/**
 * Het soort melding. De client kiest er een icoon en een bestemming bij: een bericht opent de
 * tijdlijn, een persoonlijk bericht het gesprek, een vermelding het bericht waar de reactie onder
 * staat. Later komen documenten en opleidingen erbij.
 */
export const notificationTypeSchema = z.enum([
  'post_published',
  'post_acknowledgement_reminder',
  'message_received',
  'comment_mention',
  'emergency_alert',
  // Melden vanaf de vloer (Stap 36): een nieuwe melding voor wie ze opvolgt, en de terugkoppeling
  // voor de melder. Twee soorten en niet één, want het zijn twee lezers met een andere vraag.
  'floor_report_new',
  'floor_report_update',
  // Checklists (Stap 38): een die klaarstaat voor jouw ploeg, en een die te laat is.
  'checklist_due',
  'checklist_overdue',
  // Agenda (Extra 6, punt 3): er staat een evenement klaar waar jij bij hoort.
  'agenda_item_new',
  // Documenten (Extra 6, punt 4): je hebt een versie nog niet bevestigd.
  'document_reminder',
  // Escalatie naar de leidinggevende (Stap 17): mensen uit jouw team hebben een verplicht
  // bericht na de afgesproken termijn nog niet bevestigd. Hoogstens één per bericht.
  'team_escalation',
  // Ploegoverdracht (Stap 39): de volgende ploeg begint, en de overdracht van de vorige staat klaar.
  'handover_due',
  // Goedkeuring (Stap 15): een bericht wacht op jou, en de uitkomst voor wie het indiende.
  'post_submitted',
  'post_approved',
  'post_rejected',
  // Meldingsvoorkeuren (Stap 16): iemand reageerde op jouw bericht, zonder je te vermelden.
  'comment_reply',
]);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

export const notificationChannelSchema = z.enum(['inbox', 'push', 'email']);
export type NotificationChannel = z.infer<typeof notificationChannelSchema>;

export const deliveryStatusSchema = z.enum(['queued', 'sent', 'failed', 'skipped']);
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;

/**
 * Eén regel in de inbox.
 *
 * `title` is de titel van het bericht en geen samenvatting ervan: de inbox staat achter de
 * aanmelding, dus daar mag hij staan. `refId` wijst naar het bericht; de tekst haalt de client
 * daar op, zodat de zichtbaarheidsregels van `communication` gelden en niet die van een kopie.
 */
export const inboxItemSchema = z.object({
  id: z.guid(),
  type: notificationTypeSchema,
  refModule: z.string(),
  refId: z.guid(),
  title: z.string(),
  /** Bijvoorbeeld de bedrijfsnaam; genoeg voor een regel, niet de inhoud van het bericht. */
  context: z.string(),
  requiresAcknowledgement: z.boolean(),
  readAt: z.string().datetime().nullable(),
  /** Het bericht is ingetrokken: de regel blijft staan, maar hij opent niet meer. */
  withdrawnAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type InboxItem = z.infer<typeof inboxItemSchema>;

export const inboxPageSchema = z.object({
  items: z.array(inboxItemSchema),
  nextCursor: z.string().nullable(),
});
export type InboxPage = z.infer<typeof inboxPageSchema>;

export const inboxQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type InboxQuery = z.infer<typeof inboxQuerySchema>;

export const unreadCountSchema = z.object({ unread: z.number().int() });
export type UnreadCount = z.infer<typeof unreadCountSchema>;

// --- Web push --------------------------------------------------------------------------

/**
 * De publieke VAPID-sleutel, waarmee de browser een abonnement aanmaakt.
 *
 * Publiek in de letterlijke zin: hij hoort in de client te staan. De private helft staat in
 * de secrets van de server en verlaat die nooit.
 */
export const vapidKeySchema = z.object({ publicKey: z.string() });
export type VapidKey = z.infer<typeof vapidKeySchema>;

/**
 * Een pushabonnement van één toestel, zoals de browser het teruggeeft.
 *
 * Per toestel, niet per persoon: iemand met een telefoon en een tablet krijgt de melding op
 * allebei, en een toestel dat hij inlevert wordt apart uitgeschakeld.
 */
export const pushSubscriptionRequestSchema = z.object({
  endpoint: z.string().url().max(1000),
  keys: z.object({
    p256dh: z.string().min(1).max(255),
    auth: z.string().min(1).max(255),
  }),
  /** Waarmee de medewerker zijn toestellen uit elkaar houdt in het instellingenscherm. */
  deviceLabel: z.string().max(100).optional(),
});
export type PushSubscriptionRequest = z.infer<typeof pushSubscriptionRequestSchema>;

/**
 * Wat aanmelden teruggeeft: het id van dit abonnement.
 *
 * De client heeft dat nodig om zijn eigen toestel weer uit te kunnen zetten. Aanmelden is
 * idempotent op het adres van de pushdienst, dus twee keer aanmelden geeft twee keer hetzelfde
 * id en nooit een tweede rij.
 */
export const pushSubscriptionCreatedSchema = z.object({ id: z.guid() });
export type PushSubscriptionCreated = z.infer<typeof pushSubscriptionCreatedSchema>;

/**
 * Een toestel van de native app (MOB-1): het registratietoken dat Firebase de app gaf.
 *
 * Idempotent op het token, net als web push op het adres: de app meldt het bij elke start opnieuw
 * aan, en dat levert nooit een tweede rij op.
 */
export const nativeDeviceRequestSchema = z.object({
  token: z.string().min(20).max(4096),
  platform: z.enum(['android', 'ios']),
  appVersion: z.string().max(50).optional(),
  deviceLabel: z.string().max(100).optional(),
});
export type NativeDeviceRequest = z.infer<typeof nativeDeviceRequestSchema>;

/** Afmelden van een native toestel: het token staat in de body, niet in de URL (logs). */
export const nativeDeviceRemoveRequestSchema = z.object({ token: z.string().min(20).max(4096) });
export type NativeDeviceRemoveRequest = z.infer<typeof nativeDeviceRemoveRequestSchema>;

export const pushSubscriptionSchema = z.object({
  id: z.guid(),
  deviceLabel: z.string(),
  /** `web` voor een browser, `fcm` voor de native app (MOB-1). */
  channel: z.enum(['web', 'fcm']),
  platform: z.enum(['android', 'ios']).nullable(),
  createdAt: z.string().datetime(),
  lastSuccessAt: z.string().datetime().nullable(),
  disabledAt: z.string().datetime().nullable(),
  disabledReason: z.string().nullable(),
});
export type PushSubscription = z.infer<typeof pushSubscriptionSchema>;

export const pushSubscriptionListSchema = z.object({
  items: z.array(pushSubscriptionSchema),
});
export type PushSubscriptionList = z.infer<typeof pushSubscriptionListSchema>;

/**
 * Wat er werkelijk in een pushmelding zit (ADR-0038).
 *
 * Geen berichttekst, geen namen: een type, waar het over gaat, en een korte regel die de
 * gebruiker al kent uit de meldingsinstelling ("Nieuw bericht van Demo Productie"). De inhoud
 * komt pas in beeld na het openen van de app, en dus na de aanmelding.
 */
export const pushPayloadSchema = z.object({
  type: notificationTypeSchema,
  /**
   * Waar de melding over gaat, als module plus id.
   *
   * Dit heette `postId` tot Stap 35. Het noodkanaal verwijst naar een incident en niet naar een
   * bericht, en een veld `postId` met een incident-id erin is een naam die liegt - de service
   * worker zou er een berichtpagina van maken die niet bestaat.
   */
  refModule: z.string(),
  refId: z.guid(),
  title: z.string(),
  body: z.string(),
});
export type PushPayload = z.infer<typeof pushPayloadSchema>;

/** Hoeveel toestellen van een medewerker push aan hebben staan; voor het scherm van HR. */
export const pushStatusSchema = z.object({
  personId: z.guid(),
  activeDevices: z.number().int(),
});
export type PushStatus = z.infer<typeof pushStatusSchema>;

export const pushStatusListSchema = z.object({ items: z.array(pushStatusSchema) });
export type PushStatusList = z.infer<typeof pushStatusListSchema>;

// --- Meldingsvoorkeuren (Stap 16) ---------------------------------------------------------

/**
 * De categorieën waarin een medewerker meldingen aan- of uitzet.
 *
 * Een vaste lijst die de meldingssoorten groepeert, en geen lijst per bedrijf: een medewerker hoort
 * in elk bedrijf dezelfde zeven knoppen te zien. Wat altijd doorkomt, staat er niet in - een
 * noodmelding, en een bericht dat de schrijver verplicht maakte.
 */
export const preferenceCategorySchema = z.enum([
  'news',
  'safety',
  'reminders',
  'planning',
  'conversations',
  'reports',
  'work',
]);
export type PreferenceCategory = z.infer<typeof preferenceCategorySchema>;

export const categoryPreferenceSchema = z.object({
  category: preferenceCategorySchema,
  push: z.boolean(),
  email: z.boolean(),
});
export type CategoryPreference = z.infer<typeof categoryPreferenceSchema>;

export const digestSettingSchema = z.object({
  enabled: z.boolean(),
  /** Het uur (0-23) in de tijdzone van het bedrijf. */
  hour: z.number().int().min(0).max(23),
});
export type DigestSetting = z.infer<typeof digestSettingSchema>;

export const notificationPreferencesSchema = z.object({
  categories: z.array(categoryPreferenceSchema),
  digest: digestSettingSchema,
  /** Of "Nu een samenvatting sturen" mag (alleen in de demo). */
  digestNowAllowed: z.boolean(),
  /** Of er een e-mailadres is; zonder adres heeft e-mail geen zin en toont het scherm dat. */
  hasEmail: z.boolean(),
});
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

export const updateNotificationPreferencesRequestSchema = z.object({
  categories: z.array(categoryPreferenceSchema).max(7).optional(),
  digest: digestSettingSchema.optional(),
});
export type UpdateNotificationPreferencesRequest = z.infer<
  typeof updateNotificationPreferencesRequestSchema
>;

export const digestResultSchema = z.object({ items: z.number().int(), sent: z.boolean() });
export type DigestResult = z.infer<typeof digestResultSchema>;
